import { Components } from '.generated/templates/Feature.Components.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Data as Dataconstants } from '.generated/templates/Feature.Data.constants';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { guidEquals } from 'lib/sitecore/normalize-guid';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { Field, LinkField, Image as SitecoreImage } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import { pageorLink, NavigationLink } from '../Footer/Footer';
import ArrowDown from './SvgArrowDown/arrow-down';
import { useEffect, useState } from 'react';
import Search from './SvgSearch/search';
import Menu from './SvgMenu/menu';
import MenuClose from './SvgMenuClose/menu-close';
import Container from 'components/helpers/Container';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import RitnHeader, { RitnHeaderProps } from 'components/helpers/Ritn/Header/RitnHeader';
import { useIsNRC, IsNmdpSite } from 'lib/util/site-configuration';
import NrcHeader, { NrcHeaderProps } from 'components/helpers/NRC/Header/NRCHeader';
import NmdpSiteHeader, { NmdpHeaderProps } from 'components/helpers/NmdpSite/Header/NmdpSiteHeader';

type navigationHeader = Sitecore.Override.ItemEx &
  Data.Navigation.Fields.NavigationHeader & { children?: NavigationLink[] };
type headerOrLink = (NavigationLink | navigationHeader) & { templateId: string };
export type HeaderProps = {
  fields?: {
    root?: Components.Site.Fields.Header & {
      utilityLinks?: pageorLink[];
      children?: headerOrLink[] & {
        children?: NavigationLink[];
      };
    };
  };
};
const Header = ({ fields }: HeaderProps): JSX.Element => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const isNrc = useIsNRC();
  const [subMenuIsOpen, setsubMenuIsOpen] = useState({
    status: false,
    from: '',
  });
  const isRitn = fields?.root?.fields?.isritn?.value;
  const nrcfields = { fields } as NrcHeaderProps;
  const placeholderText = '';
  const ritnFields = { ritnroot: fields?.root } as RitnHeaderProps;
  const nmdpFields = { fields } as NmdpHeaderProps;

  // Reset when path changes
  useEffect(() => {
    setIsOpen(false);
    setsubMenuIsOpen({
      status: false,
      from: '',
    });
  }, [router?.asPath]);

  const root = fields?.root;

  if (!fields) {
    return <MissingDataSource />;
  }

  const gradientBorder = classnames(
    'hidden',
    'absolute',
    'left-0',
    'bottom-0',
    'w-full',
    'h-1',
    'border-0',
    'bg-gradient-to-r',
    'from-blue',
    'to-green'
  );
  const menuBar = classnames(
    'xl:flex',
    'justify-start',
    'font-semibold',
    'leading-3',
    'text-blue',
    'text-base',
    'border-0',
    'xl:border-b',
    'border-gray-quartz'
  );
  const dropMenuItem = classnames('py-1.5', 'p-10', 'xl:py-4', 'xl:pt-6', 'xl:pb-0', 'xl:pl-24');
  const hasSubmenu = classnames('has-submenu', 'border-b', 'border-gray-quartz', 'xl:border-0');
  const noSubmenu = classnames(
    'text-theme-btn-link-text',
    'py-4',
    'xl:py-6',
    'px-4',
    'relative',
    'border-b',
    'border-gray-quartz',
    'xl:border-0'
  );
  const subMenuBtn = classnames(
    'font-semibold',
    'leading-3',
    'text-base',
    'w-full',
    'text-left',
    'xl:border-0',
    'py-4',
    'xl:py-6',
    'px-4',
    'hover:cursor-pointer',
    'relative',
    'text-theme-btn-link-text',
    'hover:text-blue-navy'
  );
  const subMenu = classnames(
    'z-20',
    'bg-white',
    'border-gray-quartz',
    'hidden',
    'w-screen',
    'max-w-screen-2xl',
    'mx-auto',
    'relative',
    'left-0',
    'right-0',
    'py-2',
    'xl:py-1.5',
    'xl:absolute',
    'xl:pt-6',
    'xl:pb-12',
    'xl:px-10',
    'xl:px-20',
    'xl:shadow-xl',
    'xl:border-t',
    'xl:columns-3',
    'xl:break-inside-avoid-column'
  );

  const toggleMobileMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const toggleSubMenu = (navId: string | '') => {
    setsubMenuIsOpen({ status: !subMenuIsOpen?.status, from: navId });
  };

  const utilityLinks = fields.root?.fields?.utilityLinks;
  const getHeaderData = root?.children?.map((item, idxHeaderLink) => {
    const navwithDropMenu = guidEquals(item.templateId, Dataconstants.TemplateIds.NavigationHeader)
      ? (item as navigationHeader)
      : null;
    const navwithoutDropMenu = guidEquals(item.templateId, Dataconstants.TemplateIds.NavigationLink)
      ? (item as NavigationLink)
      : null;
    const makeNavMenuId = (child?: string) =>
      child ? `nav-menu-header-${idxHeaderLink}-${child}` : `nav-menu-header-${idxHeaderLink}`;
    return navwithoutDropMenu ? (
      <li key={navwithoutDropMenu?.id} className={classnames(noSubmenu)}>
        <LinkA11yWrapper
          editable={false}
          field={navwithoutDropMenu.fields.navigationItemLink}
          id={`nav-links--${navwithoutDropMenu.id}`}
        />
        <span className={classnames(gradientBorder)} />
      </li>
    ) : (
      <li
        role="menuitem"
        key={makeNavMenuId()}
        className={
          subMenuIsOpen?.status && subMenuIsOpen?.from === `nav-item--${navwithDropMenu?.id}`
            ? classnames(hasSubmenu, 'state-open')
            : classnames(hasSubmenu)
        }
      >
        <button
          aria-label="Toggle sub navigation menu"
          type="button"
          role="tab"
          aria-selected="true"
          aria-controls={makeNavMenuId('menu-item')}
          onClick={() => toggleSubMenu(`nav-item--${navwithDropMenu?.id}`)}
          className={classnames(subMenuBtn)}
        >
          {navwithDropMenu?.fields.navigationHeading.value}
          <ArrowDown
            aria-hidden="true"
            id={makeNavMenuId('menu-item')}
            className={classnames(
              'svg-arrow-down',
              'inline-block',
              'ml-2',
              'float-right',
              'xl:float-none'
            )}
          />
          <span className={classnames(gradientBorder)} />
        </button>
        <ul role="menu" className={classnames(subMenu)}>
          {navwithDropMenu?.children?.map((item2, idxHeaderDropLink) => {
            const navDropItem = item2;
            const linkElementId = makeNavMenuId(
              `nav-links--${navDropItem.id ?? idxHeaderDropLink}`
            );
            const linkListId = makeNavMenuId(
              `nav-links-list-${navDropItem.id ?? idxHeaderDropLink}`
            );
            return (
              <li
                id={linkListId}
                role="menuitem"
                key={linkListId}
                className={classnames(dropMenuItem)}
                aria-label={navDropItem.fields.navigationItemLink?.value?.text}
              >
                <LinkA11yWrapper
                  editable={false}
                  field={navDropItem.fields.navigationItemLink}
                  id={linkElementId}
                />
              </li>
            );
          })}
        </ul>
      </li>
    );
  });

  const getSiteHeader = () => {
    if (IsNmdpSite()) return <NmdpSiteHeader {...nmdpFields} />;
    return <NrcHeader {...nrcfields} />;
  };

  return (
    <div
      data-component="authorable/site/header"
      className={classnames('flex', 'flex-col', 'h-auto')}
    >
      {isRitn ? (
        <RitnHeader {...ritnFields} />
      ) : (
        <>
          {isNrc || IsNmdpSite() ? (
            getSiteHeader()
          ) : (
            <>
              <div
                className={classnames('xl:hidden', 'xl:block', 'border-b', 'border-gray-quartz')}
              >
                <div className={classnames('bg-blue-navy')}>
                  <NextLink href="/">
                    <a href="/" aria-label="Open Homepage">
                      <SitecoreImage
                        field={fields.root?.fields?.logo}
                        className={classnames('h-2/4', 'py-1.5', 'px-4')}
                      />
                    </a>
                  </NextLink>
                </div>
                <div className={classnames('flex', 'justify-between', 'items-center')}>
                  <div className={classnames('self-start')}>
                    <button
                      type="button"
                      aria-label="Toggle navigation menu"
                      className={classnames(
                        'bg-white',
                        'relative',
                        'flex',
                        'flex-row',
                        'float-right',
                        'm-4',
                        'h-7',
                        'z-20'
                      )}
                      data-id="header-mobile-menu-toggle"
                      onClick={toggleMobileMenu}
                    >
                      <div
                        className={classnames(
                          'flex',
                          'justify-around',
                          'w-full',
                          'text-right',
                          'align-middle',
                          'text-body',
                          'text-blue',
                          'hover:text-blue-navy',
                          'font-semibold'
                        )}
                      >
                        {isOpen ? (
                          <span className={classnames('px-2.5', 'leading-2')}>Close</span>
                        ) : (
                          <span className={classnames('px-2.5', 'leading-2')}>Menu</span>
                        )}
                        {isOpen ? <MenuClose /> : <Menu />}
                      </div>
                    </button>
                  </div>
                  <div className={classnames('self-start')}>
                    <button
                      type="button"
                      aria-label="Toggle navigation menu"
                      className={classnames(
                        'bg-white',
                        'relative',
                        'flex',
                        'flex-row',
                        'float-right',
                        'm-4',
                        'h-7',
                        'z-20'
                      )}
                      data-id="header-mobile-menu-toggle"
                      onClick={toggleMobileMenu}
                    >
                      <div
                        className={classnames(
                          'flex',
                          'justify-around',
                          'w-full',
                          'text-right',
                          'align-middle',
                          'pr-3',
                          'text-body',
                          'text-blue',
                          'hover:text-blue-navy',
                          'font-semibold'
                        )}
                      >
                        {isOpen ? (
                          ''
                        ) : (
                          <span className={classnames('px-2.5', 'leading-2')}>Search</span>
                        )}
                        {isOpen ? (
                          ''
                        ) : (
                          <div
                            className={classnames(
                              'aa-IconWrapper',
                              'h-6',
                              'w-6',
                              'overflow-hidden'
                            )}
                          >
                            <Search searchplaceholderText={placeholderText} />
                          </div>
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              <div
                className={classnames('xl:block', 'relative', { block: isOpen, hidden: !isOpen })}
              >
                <nav
                  aria-label="Be the match utility navigation"
                  className={classnames(
                    'max-w-full',
                    'w-full',
                    'xl:mx-auto',
                    'bg-blue-navy',
                    'flex',
                    'flex-col',
                    'xl:flex-row',
                    'xl:h-12',
                    'overflow-hidden',
                    'xl:items-center'
                  )}
                >
                  <Container
                    size="standard"
                    className={classnames(
                      'w-full',
                      'xl:mx-auto',
                      'flex',
                      'flex-col',
                      'xl:flex-row',
                      'xl:h-12',
                      'overflow-hidden',
                      'xl:items-center'
                    )}
                  >
                    <NextLink href="/">
                      <a href="/" aria-label="Open Homepage">
                        <SitecoreImage
                          field={fields.root?.fields?.logo}
                          className={classnames('hidden', 'xl:block', 'px-4', 'min-w-[250px]')}
                        />
                      </a>
                    </NextLink>
                    <ul
                      role="menu"
                      className={classnames(
                        'hidden',
                        'bg-blue-navy',
                        'text-white',
                        'xl:flex',
                        'xl:top-auto',
                        'xl:relative',
                        'xl:flex-1',
                        'xl:flex-row',
                        'justify-end'
                      )}
                    >
                      {utilityLinks?.map((item) => {
                        const itemDisplayName = (item.fields.pageTitle as Field<string>)?.value
                          ? (item.fields.pageTitle as Field<string>).value
                          : item.displayName ?? 'empty-utility-nav';
                        const itemText = item.fields.navigationItemLink
                          ? (item.fields.navigationItemLink as LinkField).value?.text
                          : itemDisplayName;

                        return (
                          <li
                            role="menuitem"
                            key={item.name}
                            className={classnames('py-3')}
                            aria-label={itemText}
                          >
                            <LinkA11yWrapper
                              className={classnames(
                                'block',
                                'font-semibold',
                                'text-theme-btn-link-text',
                                'leading-3',
                                'xl:text-sm',
                                'xl:leading-3',
                                'xl:px-4',
                                'hover:underline',
                                'text-white'
                              )}
                              editable={false}
                              field={
                                item.fields.navigationItemLink
                                  ? (item.fields.navigationItemLink as LinkField)
                                  : {
                                      value: {
                                        href: item.url,
                                        text: itemText,
                                      },
                                    }
                              }
                            />
                          </li>
                        );
                      })}
                    </ul>
                    <div
                      className={classnames(
                        'relative',
                        'bg-gradient-to-r',
                        'from-blue',
                        'to-green',
                        'p-0',
                        'xl:bg-none',
                        'xl:pr-2.5'
                      )}
                    >
                      <Search searchplaceholderText={placeholderText} />
                    </div>
                  </Container>
                </nav>

                <nav
                  aria-label="Be the match main navigation"
                  className={classnames('max-w-screen-2xl', 'w-full', 'xl:mx-auto')}
                >
                  <ul
                    className={classnames(menuBar)}
                    role="menubar"
                    aria-label="Be the match main menu"
                  >
                    {getHeaderData}
                  </ul>
                </nav>
                <nav
                  aria-label="Be the match utility mobile navigation"
                  className={classnames(
                    'max-w-screen-2xl',
                    'w-full',
                    'bg-blue-navy',
                    'flex',
                    'flex-col',
                    'overflow-hidden',
                    'xl:hidden'
                  )}
                >
                  <ul
                    role="menu"
                    className={classnames(
                      'flex',
                      'flex-col',
                      'w-full',
                      'px-4',
                      'bg-blue-navy',
                      'text-white',
                      'justify-end'
                    )}
                  >
                    {utilityLinks?.map((item) => {
                      const itemDisplayName = (item.fields.pageTitle as Field<string>)?.value
                        ? (item.fields.pageTitle as Field<string>).value
                        : item.displayName;
                      const itemText = item.fields.navigationItemLink
                        ? (item.fields.navigationItemLink as LinkField).value?.text
                        : itemDisplayName;
                      return (
                        <li
                          role="menuitem"
                          key={item.name}
                          className={classnames('py-3')}
                          aria-label={itemText}
                        >
                          <LinkA11yWrapper
                            className={classnames(
                              'block',
                              'font-semibold',
                              'text-theme-btn-link-text',
                              'leading-3',
                              'hover:underline',
                              'text-white',
                              'xl:pr-5'
                            )}
                            editable={false}
                            field={
                              item.fields.navigationItemLink
                                ? (item.fields.navigationItemLink as LinkField)
                                : {
                                    value: {
                                      href: item.url,
                                      text: itemText,
                                    },
                                  }
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Header;
